<template>
  <div
    class="directory-navigation"
    :style="{ flexDirection: reversed ? 'column-reverse' : 'column' }"
  >
    <OverlayCard
      v-if="showConfirmDeleteModal"
      class="overlay-confirm-delete"
      :class="{
        'overlay-confirm-delete__fullscreen': fullscreen
      }"
      @closeOverlay="fileManager.resetCommand"
    >
      <ConfirmDeleteAttachmentCard
        :buttonText="t('general__text__delete')"
        @closeOverlay="fileManager.resetCommand"
        @confirmDelete="confirmDeleteAction"
      />
    </OverlayCard>
    <UploadModal
      :current-directory="fileManager.currentDirectory.value"
      :open="showUploadModal"
      :file-entries="droppedFiles"
      @close="fileManager.resetCommand"
      @save="handleSaveUpload"
    />
    <SettingsModal :open="showSettingsModal" @close="fileManager.resetCommand" />
    <BasicTextInputModal
      :title="t('general__text__new-directory')"
      :submit-button-text="t('general__text__create-directory')"
      :open="showNewDirectoryModal"
      :inputs="[{ key: 'name', label: t('general__text__name'), required: true }]"
      @close="fileManager.resetCommand"
      @submit="handleSubmitNewDirectory"
    />
    <BasicTextInputModal
      :title="t('general__text__new-link')"
      :submit-button-text="t('general__text__create-link')"
      :open="showNewLinkModal"
      :inputs="[
        {
          key: 'name',
          label: t('general__text__name'),
          required: true,
          validations: [
            {
              validator: (value) => value !== '',
              message: t('general__text__missing-name')
            },
            {
              validator: (value) => typeof value === 'string' && value.length <= 255,
              message: t('general__data__file-name-too-long')
            }
          ]
        },
        {
          key: 'url',
          label: 'URL',
          required: true,
          validations: [
            {
              validator: (value) => value !== '',
              message: `${t('general__text__missing-url')}, ${t(
                'general__text__valid-format'
              ).toLowerCase()} 'https://www.example.com'`
            },
            {
              validator: urlValidation,
              message: `${t('general__text__invalid-url')}, ${t(
                'general__text__valid-format'
              ).toLowerCase()} 'https://www.example.com'`
            }
          ],
          helpText: `${t('general__text__valid-format')} 'https://www.example.com'`
        }
      ]"
      @close="fileManager.resetCommand"
      @submit="handleSubmitNewLink"
    />
    <FilePreviewModal
      v-if="showPreviewToDirectoryModal"
      :open="showPreviewToDirectoryModal"
      :previewFile="previewFileForModal"
      :showDetails="!fullscreen"
      @close="fileManager.resetCommand"
      @action="handleAction"
    />

    <Teleport to="body">
      <ConnectionPopup
        v-if="showConnectionPopup"
        :selectedItem="selectedItem"
        @updateConnection="handleNewConnection"
      />
    </Teleport>

    <EditModal
      v-if="selectedItem && filteredInputs"
      :inputs="filteredInputs"
      :open="showNewEditModal"
      :selectedItem="selectedItem"
      :breadcrumbs="fileManager.breadcrumbs.value"
      @close="fileManager.resetCommand"
      @submit="handelEdit"
      @openConnection="openConnectionPopup"
      @openMove="openMove"
    />

    <DirectoryItemSelectionModal
      v-if="showMoveToDirectoryModal"
      :title="actionText"
      :open="showMoveToDirectoryModal"
      :path="fileManager.breadcrumbs.value"
      :submit-button-text="t('general__text__move-here')"
      @submit="handleSubmitMoveToDirectory"
      @close="cancelMove"
    />
    <FileConflictModal
      v-if="showConflictModal"
      :open="showConflictModal"
      :disable-submit="false"
      :name="rejectedName"
      :type="conflictItemType"
      @close="fileManager.resetCommand"
      @replaceItem="() => console.warn('Replace action not supported for directories')"
      @saveCopy="handleSaveCopy()"
    />
    <div
      v-if="showBreadcrumbs && fileManager.breadcrumbs.value.length > 0"
      class="breadcrumbs-container"
    >
      <BreadcrumbFiles
        :breadcrumbs="fileManager.breadcrumbs.value"
        @onClicked="onBreadcrumbClick"
      />
    </div>

    <div v-if="showSearch" class="search-container">
      <InputSearch
        class="search-container__input"
        :placeholderText="t('general__text__search-placeholder-directory')"
        :modelValue="fileManager.searchTerm.value"
        show-search-input
        clearInput
        @emit-search-string="fileManager.handleSearch"
      />
    </div>

    <div v-if="showToolbar" class="header">
      <DirectoryToolbar
        :selectedItems="fileManager.selectedItems.value"
        :new-actions="newActions"
        :bulk-actions="itemActions"
        @action="handleAction"
      />
    </div>

    <div
      class="content"
      :class="{ 'content--fullscreen': fullscreen, 'content--preview': previewableFileSelected }"
    >
      <Transition>
        <div v-if="fileManager.isLoading.value" class="loading-overlay">
          <PulseLoader :loading="true" color="#3F46D6" />
          <TextBase
            v-if="fileManager.message.value"
            :text="fileManager.message.value"
            type="paragraph"
            style="margin-top: 16px"
          />
        </div>
      </Transition>
      <Transition>
        <div
          v-if="fileManager.state.value === 'error' && !fileManager.isLoading.value"
          class="error-message"
        >
          <TextBase
            :text="`Error: ${fileManager.message.value}`"
            type="paragraph"
            style="margin-top: 16px"
          />
          <ButtonPrimary
            :button-text="t('general__text__reload')"
            style="margin-top: 16px"
            @onClicked="retryFetch"
          />
        </div>
      </Transition>
      <Transition>
        <div v-if="fileManager.state.value === 'message'" class="info-message">
          <TextBase :text="fileManager.message.value" type="paragraph" />
        </div>
      </Transition>
      <InputFileDropBase class="content__drop-zone" @items-dropped="handleDroppedFileItems">
        <template #content>
          <DirectoryListView
            :headers="fileManager.sortHeaders.value"
            :directoryItems="fileManager.displayedItems.value"
            :actions="filteredListViewActions"
            :currentItemId="currentItemId"
            :drag-drop-enabled="props.dragDropEnabled"
            :multi-select-enabled="props.multiSelectEnabled"
            :showFooter="fullscreen"
            show-headers
            @action="handleAction"
            @select="fileManager.handleSelectionChange"
            @doubleClick="fileManager.openItem"
            @itemsDroppedOnTarget="
              ({ draggedItemIds, targetItemId }) =>
                fileManager.handleDrop(draggedItemIds, targetItemId)
            "
            @sort="fileManager.handleSort"
            @load-more-items="fileManager.loadMoreItems"
          />
        </template>
      </InputFileDropBase>
      <div v-if="fullscreen" class="footer">
        <TextBase
          type="paragraph"
          class="footer-text"
          :text="`${fileManager.currentPage.value?.totalElements ?? '-'} ${t(
            'general__text__items'
          ).toLowerCase()}`"
        />
        <TextBase
          v-if="
            fileManager.selectedItems.value.length > 0 &&
            fileManager.displayedItems.value.length > 0
          "
          type="paragraph"
          class="footer-text"
          :text="`${fileManager.selectedItems.value.length}  ${t(
            'general__text__have-been-chosen'
          ).toLowerCase()}`"
        />
      </div>
      <div
        v-if="previewableFileSelected && previewFileFullscreen && fullscreen"
        class="preview-container"
      >
        <FilePreview
          :preview-file="previewFileFullscreen"
          :preview-type="previewType"
          :file-blob-source="blobUrl"
          :text-content="textContent"
          :loading-file="isLoading"
          :file-error-text="errorMessage"
          :fullscreen="fullscreen"
        ></FilePreview>
        <TextBase type="paragraph" weight="bold" :text="previewFileFullscreen.name" />
        <FileMetaInfo :directory-item="previewFileFullscreen" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, toRefs, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import DirectoryToolbar from './DirectoryToolbar.vue'
import InputSearch from '@/design-system/molecules/InputSearch.vue'
import FilePreviewModal from './FilePreviewModal.vue'
import BreadcrumbFiles from './BreadcrumbFiles.vue'
import { isError, type DirectoryItem } from './types'
import BasicTextInputModal from './BasicTextInputModal.vue'
import DirectoryListView from './list-view/DirectoryListView.vue'
import EditModal from './EditModal.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import DirectoryItemSelectionModal from './DirectoryItemSelectionModal.vue'
import { CreateNewAction } from './actions/createNewAction'
import { MoveAction } from './actions/moveAction'
import { EditAction } from './actions/editAction'
import FileConflictModal from './FileConflictModal.vue'
import UploadModal from './UploadModal.vue'
import { useGeneralStore } from '@/pinia'
import ConnectionPopup from './ConnectionPopup.vue'
import { CoreEntityDto } from '@/interfaces/core.interface'
import InputFileDropBase from './InputFileDropBase.vue'
import SettingsModal from './SettingsModal.vue'
import { useActions } from './actionStore'
import { storeToRefs } from 'pinia'
import FileMetaInfo from './FileMetaInfo.vue'
import FilePreview from './FilePreview.vue'
import { postDirectoryItem } from './files.service'
import { useFileManagerConnected } from './useFileManagerConnected'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import ConfirmDeleteAttachmentCard from '../attachments/ConfirmDeleteAttachmentCard.vue'
import { DeleteAction } from './actions/deleteAction'
import { useFilePreview } from './useFilePreview'

type Props = {
  showSearch?: boolean
  showBreadcrumbs?: boolean
  showToolbar?: boolean
  dragDropEnabled?: boolean
  multiSelectEnabled?: boolean
  fullscreen: boolean
  reversed?: boolean
  enabledActions?: string[]
}

const props = defineProps<Props>()
const { fullscreen } = toRefs(props)

const { t } = useI18n()

const fileManager = useFileManagerConnected()
const { newActions, itemActions, listViewActions } = storeToRefs(useActions())

const generalStore = useGeneralStore()
const { showConnectionPopup } = toRefs(generalStore)
const cameFromEdit: Ref<boolean> = ref(false)
const previewableFileSelected = ref<boolean>(false)
const previewFileFullscreen = ref<DirectoryItem<'file'> | undefined>(undefined)
const rawInputs = [
  { key: 'name', label: t('general__text__name'), required: true, type: 'textstandard' },
  { key: 'url', label: 'URL', required: true, type: 'textstandard' },
  { key: 'Beskrivning', label: t('general__text__description'), required: true, type: 'textarea' }
]
const conflictingItems = ref<boolean>(false)
const rejectedName = ref<string>('')
const suggestedName = ref<string>('')
const conflictItemType = ref<'directory' | 'file' | 'link'>('directory')
const filteredInputs = computed(() => {
  if (!selectedItem.value) return []
  if (selectedItem.value.type === 'link') {
    return rawInputs
  }
  return rawInputs.filter((input) => input.key !== 'url')
})
const showConflictModal = computed(
  () =>
    (fileManager.currentCommand.value?.action === 'new-link' ||
      fileManager.currentCommand.value?.action === 'new-directory' ||
      fileManager.currentCommand.value?.action === 'move') &&
    conflictingItems.value
)
const showConfirmDeleteModal = computed(() => fileManager.currentCommand.value?.action === 'delete')
const showUploadModal = computed(() => fileManager.currentCommand.value?.action === 'upload')
const showNewDirectoryModal = computed(
  () => fileManager.currentCommand.value?.action === 'new-directory'
)
const showNewLinkModal = computed(() => fileManager.currentCommand.value?.action === 'new-link')
const showNewEditModal = computed(() => fileManager.currentCommand.value?.action === 'edit')
const showMoveToDirectoryModal = computed(() => fileManager.currentCommand.value?.action === 'move')
const showPreviewToDirectoryModal = computed(
  () =>
    fileManager.currentCommand.value &&
    ['open-file'].includes(fileManager.currentCommand.value.action)
)
const showSettingsModal = computed(() => fileManager.currentCommand.value?.action === 'settings')
const previewFileForModal = computed(
  () => fileManager.currentCommand.value?.items[0] as DirectoryItem<'file'>
)
const currentItemId = ref<string>()
const selectedItem = computed<DirectoryItem | undefined>(() =>
  fileManager.currentCommand.value?.items.length === 1
    ? fileManager.currentCommand.value?.items[0]
    : undefined
)
const droppedFiles = ref<FileSystemEntry[]>([])

const actionText = computed<string>(() => 'Test')

const filteredListViewActions = computed(() =>
  listViewActions.value.filter((action) =>
    props.enabledActions ? props.enabledActions.includes(action.actionName) : true
  )
)

function handleSaveCopy() {
  // create a copy of the item with a new name
  if (rejectedName.value && suggestedName.value) {
    fileManager.executeAction(
      new CreateNewAction(
        [
          {
            name: suggestedName.value,
            directoryId:
              fileManager.currentDirectoryId.value === 'root'
                ? undefined
                : fileManager.currentDirectoryId.value,
            type: 'directory'
          }
        ],
        fileManager.currentDirectoryId.value,
        postDirectoryItem
      )
    )
  }
  conflictingItems.value = false
  fileManager.resetCommand()
  console.debug('Conflict action triggered')
}

const { previewType, isLoading, blobUrl, textContent, errorMessage } =
  useFilePreview(previewFileFullscreen)

watch(
  fullscreen,
  (newValue) => {
    fileManager.setFullscreen(newValue)
  },
  { immediate: true }
)
async function confirmDeleteAction() {
  if (fileManager.currentCommand.value?.items) {
    await fileManager.executeAction(new DeleteAction(fileManager.currentCommand.value?.items))
  }
}
async function handleAction(action: string, items: DirectoryItem[]) {
  fileManager.executeCommand({
    action,
    items
  })
  if (items.length === 0) {
    console.warn('Row action triggered without an item:', action)
    return
  }
  if (action === 'change-connection') {
    openConnectionPopup()
  }
  console.debug('Action clicked:', action, 'on selected items:', items)
}

function retryFetch() {
  fileManager.refresh()
}
function openConnectionPopup() {
  generalStore.setShowConnectionPopup(true)
  generalStore.setShowNoFeature(true)
}

async function handleSaveUpload() {
  fileManager.refresh()
}

async function handleSubmitNewDirectory({ name }: Record<string, string | number>) {
  const parentId = fileManager.currentDirectoryId.value
  const res = await fileManager.executeAction<{ id: string }[], undefined>(
    new CreateNewAction(
      [
        {
          name: name.toString().trim(),
          directoryId: parentId === 'root' ? undefined : parentId,
          type: 'directory'
        }
      ],
      fileManager.currentDirectoryId.value,
      postDirectoryItem
    )
  )
  if (isError(res)) {
    if (res.error.itemAlreadyExists) {
      conflictingItems.value = true
      conflictItemType.value = 'directory'
      rejectedName.value = name.toString().trim()
      suggestedName.value = res.error.suggestedName ?? `${name} (copy)`
    }
    return
  }
  currentItemId.value = res.data[0]?.id
}

async function handleSubmitNewLink({ name, url }: Record<string, string | number>) {
  const res = await fileManager.executeAction<{ id: string }[], undefined>(
    new CreateNewAction(
      [
        {
          name: name.toString().trim(),
          type: 'link',
          directoryId: fileManager.currentDirectoryId.value,
          url: url.toString().trim()
        }
      ],
      fileManager.currentDirectoryId.value,
      postDirectoryItem
    )
  )
  if (isError(res)) {
    console.error('Failed to create new link')
    return
  }
  currentItemId.value = res.data[0]?.id
}
async function handelEdit(item: DirectoryItem, edits: Record<string, string | number>) {
  fileManager.executeAction(
    new EditAction(item, {
      type: item.type,
      ...edits
    })
  )
}
async function handleNewConnection(entity: CoreEntityDto) {
  if (selectedItem.value) {
    const edits = {
      type: selectedItem.value.type,
      entityConnection: entity.id
    }
    await fileManager.executeAction(new EditAction(selectedItem.value, edits))
    await fileManager.refresh()
  }
}
function onBreadcrumbClick(option: DirectoryItem) {
  if (!option.id) {
    console.warn('Breadcrumb has no id—skipping navigation', option)
    return
  }
  fileManager.openItem(option.id)
}

function handleSubmitMoveToDirectory(targetItems: DirectoryItem[]) {
  const targetId = targetItems[0]?.id
  const actionItems = fileManager.currentCommand.value?.items
  if (actionItems) {
    fileManager.executeAction(new MoveAction(actionItems, targetId))
  }
}

const urlValidation = (url: string | number) => {
  // only allow https URLs
  const urlPattern = /^(https:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w- ./?%&=]*)?$/i
  return urlPattern.test(url.toString())
}

function openMove() {
  if (!selectedItem.value) return
  cameFromEdit.value = fileManager.currentCommand.value?.action === 'edit'
  fileManager.executeCommand({
    action: 'move',
    items: [selectedItem.value]
  })
}

function cancelMove() {
  if (cameFromEdit.value && selectedItem.value) {
    fileManager.executeCommand({
      action: 'edit',
      items: [selectedItem.value]
    })
  } else {
    fileManager.resetCommand()
  }
  cameFromEdit.value = false
}

async function handleDroppedFileItems(items: DataTransferItem[]) {
  droppedFiles.value = items
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => entry !== null)
  await fileManager.executeCommand({
    action: 'upload',
    items: []
  })
}

watch(
  fileManager.selectedItems,
  (newValue) => {
    if (newValue.length === 1 && newValue[0].type === 'file') {
      previewFileFullscreen.value = newValue[0] as DirectoryItem<'file'>
      previewableFileSelected.value = true
    } else {
      previewFileFullscreen.value = undefined
      previewableFileSelected.value = false
    }
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins.scss';

.directory-navigation {
  display: flex;
  width: 100%;
  height: 100%;

  .overlay-confirm-delete {
    left: -72px;
    top: -16px;
    &__fullscreen {
      left: 0;
      top: 0;
    }
  }

  .search-container {
    display: flex;
    height: 40px;
    margin: 8px 0 16px;
    flex-shrink: 0;
    align-items: center;
    z-index: 10;
    &__input {
      padding: 0 24px;
      width: 100%;
      :deep(.text-field-standard) {
        height: 40px;
      }
    }
  }

  .breadcrumbs-container {
    padding: 8px 24px;
    margin-top: 8px;
    height: 40px;
    align-self: flex-start;
    display: flex;
    align-items: center;
    font-size: 0.9em;
    flex-shrink: 0;
    white-space: nowrap;
    z-index: 100;
    width: 100%;

    a {
      color: #3f46d6;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
    .separator {
      margin: 0 0.5em;
      color: #6c757d;
    }
  }

  .header {
    flex-shrink: 0;
  }

  .content {
    flex-grow: 1;
    position: relative;
    min-height: 0;
    display: grid;
    height: 100%;

    grid-template-areas: 'list';
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;

    &--fullscreen {
      &:not(.content--preview) {
        grid-template-areas:
          'list list'
          'footer footer';
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr auto;
      }

      &.content--preview {
        grid-template-areas:
          'list list preview'
          'footer footer preview';
        grid-template-columns: 1fr 1fr 415px;
        grid-template-rows: 1fr auto;
      }
    }

    &__drop-zone {
      grid-area: list;
      overflow-y: auto;
    }
    .footer {
      grid-area: footer;
      display: flex;
      background-color: #fff;
      padding: 16px 24px 24px 24px;
      align-items: center;
      gap: 32px;
      align-self: stretch;
      width: 100%;
      bottom: 0;
    }
    .preview-container {
      grid-area: preview;
      width: 100%;
      display: flex;
      flex-direction: column;
      max-width: 415px;
      padding: 16px 16px 24px;
      gap: 16px;
      height: 100%;
      overflow-y: auto;
      @include mixins.scrollbar-vertical;
      border-left: 1px solid #e0e0e0;
      .tooltip-container {
        overflow: visible;
      }
      .file-info {
        overflow: visible;
      }
    }
  }

  .loading-overlay {
    background-color: rgba(255, 255, 255, 0.8);
  }

  .info-message {
    background-color: #fff;
  }

  .loading-overlay,
  .info-message,
  .error-message {
    position: absolute;
    top: 40px;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    z-index: 20;
    padding: 20px;
    text-align: center;
    align-items: center;
  }

  .error-message {
    background-color: rgba(244, 224, 224, 0.9);
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
